import { AxiosInstance } from 'axios';
import { toast } from 'sonner';

import {
  CAPABILITIES,
  CAPABILITY_CONFIG_KEYS,
} from '@/sections/asset/constants';
import { JobConfig, ScanOptions } from '@/sections/asset/types';
import { Attribute, ResourceType } from '@/types';

/**
 * Response from the /my endpoint for WebApplication queries
 */
interface WebApplicationResponse {
  key: string;
  name?: string;
  url?: string;
}

/**
 * Response from the /my endpoint containing WebApplications
 */
interface WebAppDataResponse {
  webApplications?: WebApplicationResponse[];
}

/**
 * Response from the /my endpoint for Attribute queries
 */
interface AttributeDataResponse {
  attributes?: Attribute[];
}

/**
 * Resolves a single asset to its web application jobs
 * Handles direct WebApplication assets, child WebApplications, and HTTP attributes
 */
async function resolveWebAssetForCrawl(
  asset: { key: string },
  axios: AxiosInstance,
  options?: ScanOptions
): Promise<JobConfig[]> {
  const jobs: JobConfig[] = [];
  const randomUserAgent = options?.randomUserAgent ? 'true' : 'false';
  // Default to 'crawl' if no run types specified to avoid empty job arrays
  const runTypes =
    options?.runTypes && options.runTypes.length > 0
      ? options.runTypes
      : ['crawl'];

  try {
    if (asset.key.startsWith('#webapplication#')) {
      runTypes.forEach(runType => {
        jobs.push({
          assetKey: asset.key,
          capabilities: [CAPABILITIES.CRAWLER],
          config: {
            [CAPABILITY_CONFIG_KEYS.RANDOM_USER_AGENT]: randomUserAgent,
            [CAPABILITY_CONFIG_KEYS.RUN_TYPE]: runType,
          },
        });
      });
      return jobs;
    }

    const [webAppResult, attributeResult] = await Promise.allSettled([
      axios.get<WebAppDataResponse>('/my', {
        params: {
          label: ResourceType.WebApplication,
          key: `source:${asset.key}`,
          exact: false,
        },
      }),
      axios.get<AttributeDataResponse>('/my', {
        params: {
          label: ResourceType.Attribute,
          key: `source:${asset.key}`,
          exact: true,
        },
      }),
    ]);

    // Check for child WebApplication entities
    if (webAppResult.status === 'fulfilled') {
      const webApplications = webAppResult.value.data.webApplications ?? [];
      if (webApplications.length > 0) {
        webApplications.forEach(webapp => {
          runTypes.forEach(runType => {
            jobs.push({
              assetKey: webapp.key,
              capabilities: [CAPABILITIES.CRAWLER],
              config: {
                [CAPABILITY_CONFIG_KEYS.RANDOM_USER_AGENT]: randomUserAgent,
                [CAPABILITY_CONFIG_KEYS.RUN_TYPE]: runType,
              },
            });
          });
        });
        return jobs;
      }
    }

    // Check for HTTP/HTTPS attributes and run webapp-discovery first
    if (attributeResult.status === 'fulfilled') {
      const attributes = attributeResult.value.data.attributes ?? [];
      const httpAttributes = attributes.filter(
        attr => attr.name === 'http' || attr.name === 'https'
      );

      if (httpAttributes.length > 0) {
        httpAttributes.forEach(attr => {
          runTypes.forEach(runType => {
            jobs.push({
              assetKey: attr.key,
              capabilities: [
                CAPABILITIES.WEBAPP_DISCOVERY,
                CAPABILITIES.CRAWLER,
              ],
              config: {
                [CAPABILITY_CONFIG_KEYS.RANDOM_USER_AGENT]: randomUserAgent,
                [CAPABILITY_CONFIG_KEYS.RUN_TYPE]: runType,
              },
            });
          });
        });
      }
    }

    return jobs;
  } catch (error) {
    toast.error(`Failed to fetch data for asset ${asset.key}`);
    return [];
  }
}

/**
 * Resolves a single asset to its web scan jobs
 * Handles direct WebApplication assets, child WebApplications, and HTTP attributes
 */
async function resolveWebAssetForScan(
  asset: { key: string },
  axios: AxiosInstance
): Promise<JobConfig[]> {
  const jobs: JobConfig[] = [];

  try {
    // Check for direct WebApplication targets
    if (asset.key.startsWith('#webapplication#')) {
      jobs.push({
        assetKey: asset.key,
        capabilities: [CAPABILITIES.BURP_DAST_SCAN],
        config: {},
      });
      return jobs;
    }

    // Fetch child WebApplication entities and HTTP attributes in parallel
    const [webAppResult, attributeResult] = await Promise.allSettled([
      axios.get<WebAppDataResponse>('/my', {
        params: {
          label: ResourceType.WebApplication,
          key: `source:${asset.key}`,
          exact: false,
        },
      }),
      axios.get<AttributeDataResponse>('/my', {
        params: {
          label: ResourceType.Attribute,
          key: `source:${asset.key}`,
          exact: true,
        },
      }),
    ]);

    // Check for child WebApplication entities
    if (webAppResult.status === 'fulfilled') {
      const webApplications = webAppResult.value.data.webApplications ?? [];
      if (webApplications.length > 0) {
        webApplications.forEach(webapp => {
          jobs.push({
            assetKey: webapp.key,
            capabilities: [CAPABILITIES.BURP_DAST_SCAN],
            config: {},
          });
        });
        return jobs;
      }
    }

    // Check for HTTP/HTTPS attributes and run webapp-discovery first
    if (attributeResult.status === 'fulfilled') {
      const attributes = attributeResult.value.data.attributes ?? [];
      const httpAttributes = attributes.filter(
        attr => attr.name === 'http' || attr.name === 'https'
      );

      if (httpAttributes.length > 0) {
        httpAttributes.forEach(attr => {
          jobs.push({
            assetKey: attr.key,
            capabilities: [
              CAPABILITIES.WEBAPP_DISCOVERY,
              CAPABILITIES.BURP_DAST_SCAN,
            ],
            config: {},
          });
        });
      }
    }

    return jobs;
  } catch (error) {
    toast.error(`Failed to fetch data for asset ${asset.key}`);
    return [];
  }
}

/**
 * Builds crawl jobs for assets
 * Parallelizes API calls for better performance
 *
 * @param assets - Assets to crawl
 * @param axios - Axios instance for API calls
 * @param options - Scan options (random user agent, run types)
 * @returns Array of job configurations
 */
export async function buildCrawlJobs(
  assets: { key: string }[],
  axios: AxiosInstance,
  options?: ScanOptions
): Promise<JobConfig[]> {
  // Parallelize resolution of all assets
  const results = await Promise.allSettled(
    assets.map(asset => resolveWebAssetForCrawl(asset, axios, options))
  );

  // Flatten successful results
  const jobs = results
    .filter(
      (r): r is PromiseFulfilledResult<JobConfig[]> => r.status === 'fulfilled'
    )
    .flatMap(r => r.value);

  return jobs;
}

/**
 * Builds web scan jobs for assets
 * Parallelizes API calls for better performance
 *
 * @param assets - Assets to scan
 * @param axios - Axios instance for API calls
 * @returns Array of job configurations
 */
export async function buildWebScanJobs(
  assets: { key: string }[],
  axios: AxiosInstance
): Promise<JobConfig[]> {
  // Parallelize resolution of all assets
  const results = await Promise.allSettled(
    assets.map(asset => resolveWebAssetForScan(asset, axios))
  );

  // Flatten successful results
  const jobs = results
    .filter(
      (r): r is PromiseFulfilledResult<JobConfig[]> => r.status === 'fulfilled'
    )
    .flatMap(r => r.value);

  return jobs;
}
